import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import SortIcon from '@mui/icons-material/Sort';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import debounce from 'lodash/debounce';

import { SearchBar } from '@bloomays-lib/ui.shared';
import { StatusType, Role, TalentBoardEventType } from '@bloomays-lib/types.shared';
import { useAuthStore } from '@bloomays-lib/common.auth/browser';
import { useJobList } from '../../hooks/useJobList';
import { type StatusFilter } from '../../stores/jobListStore';
import { Logger } from '../../services/service-logger';

const logger = Logger('JobListHeader');

const JobListHeader = () => {
  const { t } = useTranslation(['talentBoard', 'jobs', 'random']);
  const auth = useAuthStore((state) => state.auth);
  const isClient = auth?.user?.roles?.includes(Role.Client) || false;
  const {
    searchQuery,
    setSearchQuery,
    sortAscending,
    setSortAscending,
    statusFilter,
    setStatusFilter,
    tamFilter,
    setTamFilter,
    tamList,
  } = useJobList();
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  const debouncedSetSearchQuery = useMemo(
    () =>
      debounce((value: string) => {
        logger.info('Traffic', {
          page: 'JobList',
          data: { searchQuery: value },
          evt: { name: TalentBoardEventType.TalentboardFilterJobBySearchQuery },
        });
        setSearchQuery(value);
      }, 500),
    [setSearchQuery],
  );

  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [debouncedSetSearchQuery]);

  return (
    <Container>
      <Grid container spacing={1} alignItems="center">
        <Grid size={{ xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }}>
          <SearchBar
            placeholder={t('search', { ns: 'talentBoard' })}
            searchValue={localSearchQuery}
            onChange={(e) => {
              const value = e.target.value;
              setLocalSearchQuery(value);
              debouncedSetSearchQuery(value);
            }}
            variant="talentBoard"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 2, md: 2, lg: 2, xl: 2 }}>
          <FormControl fullWidth>
            <InputLabel id="status-select-label">{t('status', { ns: 'jobs' })}</InputLabel>
            <Select
              value={statusFilter}
              label={t('status', { ns: 'jobs' })}
              labelId="status-select-label"
              onChange={(e: SelectChangeEvent<StatusFilter>) => {
                logger.info('Traffic', {
                  page: 'JobList',
                  data: { statusFilter: e.target.value },
                  evt: { name: TalentBoardEventType.TalentboardFilterJobByStatus },
                });
                setStatusFilter(e.target.value as StatusFilter);
              }}
              displayEmpty
            >
              <MenuItem value="all">{t('all', { ns: 'random' })}</MenuItem>
              <MenuItem value={StatusType.inProgress}>{t('statusInProgress', { ns: 'jobs' })}</MenuItem>
              <MenuItem value={StatusType.win}>{t('statusWin', { ns: 'jobs' })}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {!isClient && (
          <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="tam-select-label" shrink={true}>
                {t('tam', { ns: 'jobs' })}
              </InputLabel>
              <Select
                value={tamFilter || ''}
                label={t('tam', { ns: 'jobs' })}
                labelId="tam-select-label"
                onChange={(e: SelectChangeEvent<string>) => {
                  const value = e.target.value as string;
                  const finalValue = value === '' ? null : value;
                  logger.info('Traffic', {
                    page: 'JobList',
                    data: { tamFilter: finalValue },
                    evt: { name: TalentBoardEventType.TalentboardFilterJobByTam },
                  });
                  setTamFilter(finalValue);
                }}
                displayEmpty
              >
                <MenuItem value="">{t('all', { ns: 'random' })}</MenuItem>
                {tamList.map((tam) => (
                  <MenuItem key={tam.email} value={tam.email}>
                    {tam.firstname} {tam.lastname}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 2, md: 2, lg: 2, xl: 2 }}>
          <SortContainer
            onClick={() => {
              logger.info('Traffic', {
                page: 'JobList',
                data: { sortAscending: sortAscending },
                evt: { name: TalentBoardEventType.TalentboardSortJobByStartDate },
              });
              setSortAscending(!sortAscending);
            }}
          >
            <IconWrapper>
              {sortAscending ? (
                <ArrowDownwardIcon sx={{ cursor: 'pointer', fontSize: '18px' }} />
              ) : (
                <ArrowUpwardIcon sx={{ cursor: 'pointer', fontSize: '18px' }} />
              )}
              <SortIcon sx={{ cursor: 'pointer', fontSize: '18px' }} />
            </IconWrapper>
            <SortLabel variant="body2">{t('sortByStartDate')}</SortLabel>
          </SortContainer>
        </Grid>
      </Grid>
    </Container>
  );
};

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    gap: '8px',
    padding: theme.spacing(1),
  },
}));

const SortContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: 'fit-content',
  padding: theme.spacing(1, 0),
  cursor: 'pointer',
  userSelect: 'none',
  gap: theme.spacing(1),

  '&:hover': {
    opacity: 0.7,
  },

  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
    width: '100%',
  },
}));

const IconWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

const SortLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral[80],
  fontSize: '14px',
  cursor: 'pointer',
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
  },
}));

export default JobListHeader;
